import React, { useEffect, useState } from 'react';
import { toast } from 'react-toastify';
import { Eye, EyeOff, Loader2, Send, ShieldCheck, AlertTriangle } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import { iaAdminAPI } from '../services/api';

const LABEL_PROVIDER = {
  openai: 'OpenAI (GPT)',
  anthropic: 'Anthropic (Claude)',
  gemini: 'Google (Gemini)',
};

/**
 * Painel de configuração de IA embutível — recebe clienteId como prop.
 * Usado dentro de ClientDetails. Sem Layout, sem seletor de cliente.
 */
export default function IAConfigPanel({ clienteId, flat = false }) {
  const { darkMode } = useTheme();

  const [catalogo, setCatalogo] = useState({ modelos: [], plataforma_configurada: {} });
  const [configs, setConfigs] = useState([]);
  const [form, setForm] = useState(null);
  const [chaveNova, setChaveNova] = useState('');
  const [mostrarChave, setMostrarChave] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [testando, setTestando] = useState(false);
  const [resultadoTeste, setResultadoTeste] = useState(null);
  const [mensagemTeste, setMensagemTeste] = useState('Olá, quanto custa?');

  const card = flat ? '' : `rounded-lg shadow-sm border ${darkMode ? 'bg-zinc-800 border-gray-700' : 'bg-white border-gray-200'}`;
  const cardPadding = flat ? '' : 'p-4';
  const textP = darkMode ? 'text-white' : 'text-gray-900';
  const textS = darkMode ? 'text-gray-400' : 'text-gray-500';
  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm ${darkMode ? 'bg-zinc-700 border-zinc-600 text-white' : 'bg-white border-gray-300 text-gray-900'}`;
  const label = `block text-sm font-medium mb-1 ${textP}`;

  useEffect(() => {
    (async () => {
      try {
        const { data } = await iaAdminAPI.catalogo();
        setCatalogo(data || { modelos: [], plataforma_configurada: {} });
      } catch (e) {
        toast.error('Erro ao carregar catálogo de IA');
      }
    })();
  }, []);

  useEffect(() => {
    if (!clienteId) {
      setConfigs([]);
      setForm(null);
      return;
    }
    (async () => {
      try {
        const { data } = await iaAdminAPI.listarConfigs(clienteId);
        setConfigs(data || []);
        selecionar((data || [])[0] || null);
      } catch (e) {
        toast.error('Erro ao carregar configurações de IA');
      }
    })();
  }, [clienteId]);

  const selecionar = (config) => {
    setResultadoTeste(null);
    setChaveNova('');
    setForm(config ? { ...config } : null);
  };

  const modelosDoProvider = (provider) =>
    (catalogo.modelos || []).filter((m) => m.provider === provider);

  const plataformaTemChave = (provider) =>
    !!catalogo.plataforma_configurada?.[provider];

  const trocarProvider = (provider) => {
    const modelos = modelosDoProvider(provider);
    const recomendado = modelos.find((m) => m.recomendado) || modelos[0];
    setForm({
      ...form,
      provider,
      modelo: recomendado?.id || '',
      usar_chave_plataforma: plataformaTemChave(provider)
        ? form.usar_chave_plataforma
        : false,
    });
  };

  const salvar = async () => {
    setSalvando(true);
    try {
      const payload = {
        provider: form.provider,
        modelo: form.modelo,
        usar_chave_plataforma: form.usar_chave_plataforma,
        temperatura: Number(form.temperatura),
        max_tokens: Number(form.max_tokens),
      };
      if (chaveNova.trim()) payload.api_key = chaveNova.trim();

      const { data } = await iaAdminAPI.atualizarConfig(form.id, payload);
      setForm(data);
      setChaveNova('');
      setConfigs((prev) => prev.map((c) => (c.id === data.id ? data : c)));
      toast.success('Configuração de IA salva');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao salvar');
    } finally {
      setSalvando(false);
    }
  };

  const limparChave = async () => {
    setSalvando(true);
    try {
      const { data } = await iaAdminAPI.atualizarConfig(form.id, { api_key: null });
      setForm(data);
      toast.success('Chave do cliente removida; usando a da plataforma');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao remover chave');
    } finally {
      setSalvando(false);
    }
  };

  const testar = async () => {
    setTestando(true);
    setResultadoTeste(null);
    try {
      const { data } = await iaAdminAPI.testar(form.id, mensagemTeste);
      setResultadoTeste(data);
      if (!data.ok) toast.error('O teste falhou — veja o detalhe abaixo');
    } catch (e) {
      toast.error(e.response?.data?.message || 'Erro ao testar');
    } finally {
      setTestando(false);
    }
  };

  const modelos = form ? modelosDoProvider(form.provider) : [];

  if (!form) {
    return (
      <div className={`${card} ${cardPadding} ${textS} text-sm`}>
        Este cliente ainda não tem configuração de IA. Ele precisa criar o agente na tela dele antes de configurar o provedor.
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {configs.length > 1 && (
        <div>
          <label className={label}>Configuração ({configs.length})</label>
          <select
            value={form?.id || ''}
            onChange={(e) => selecionar(configs.find((c) => c.id === e.target.value))}
            className={inputCls}
          >
            {configs.map((c) => (
              <option key={c.id} value={c.id}>{c.nome}</option>
            ))}
          </select>
        </div>
      )}

      <div className={`${card} ${cardPadding} space-y-4`}>
        <h3 className={`font-semibold ${textP}`}>Provedor e modelo</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div>
            <label className={label}>Provedor de IA</label>
            <select
              value={form.provider}
              onChange={(e) => trocarProvider(e.target.value)}
              className={inputCls}
            >
              {Object.entries(LABEL_PROVIDER).map(([value, texto]) => (
                <option key={value} value={value}>
                  {texto}{!plataformaTemChave(value) ? ' — sem chave da plataforma' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={label}>Modelo</label>
            <select
              value={form.modelo}
              onChange={(e) => setForm({ ...form, modelo: e.target.value })}
              className={inputCls}
            >
              {modelos.map((m) => (
                <option key={m.id} value={m.id}>
                  {m.label}{m.recomendado ? ' (recomendado)' : ''}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className={label}>Temperatura</label>
            <input
              type="number" step="0.1" min="0" max="2"
              value={form.temperatura}
              onChange={(e) => setForm({ ...form, temperatura: e.target.value })}
              className={inputCls}
            />
          </div>

          <div>
            <label className={label}>Máximo de tokens</label>
            <input
              type="number" min="1"
              value={form.max_tokens}
              onChange={(e) => setForm({ ...form, max_tokens: e.target.value })}
              className={inputCls}
            />
          </div>
        </div>
      </div>

      <div className={`${card} ${cardPadding} space-y-4`}>
        <h3 className={`font-semibold ${textP}`}>Chave de API</h3>

        <label className="flex items-start gap-3 cursor-pointer">
          <input
            type="checkbox"
            checked={form.usar_chave_plataforma}
            disabled={!plataformaTemChave(form.provider)}
            onChange={(e) => setForm({ ...form, usar_chave_plataforma: e.target.checked })}
            className="mt-1 h-4 w-4 rounded border-gray-300"
          />
          <span className={`text-sm ${textP}`}>
            Usar a chave padrão da plataforma
            <span className={`block text-xs ${textS}`}>
              {plataformaTemChave(form.provider)
                ? 'O consumo é faturado para a Nomoz.'
                : `Indisponível: a plataforma não tem chave configurada para ${LABEL_PROVIDER[form.provider]}.`}
            </span>
          </span>
        </label>

        {!form.usar_chave_plataforma && (
          <div>
            <label className={label}>
              Chave do cliente
              {form.api_key_definida && (
                <span className={`ml-2 text-xs font-normal ${textS}`}>
                  (atual: {form.api_key_mascarada || 'já configurada'} — deixe em branco para manter)
                </span>
              )}
            </label>
            <div className="relative">
              <input
                type={mostrarChave ? 'text' : 'password'}
                value={chaveNova}
                onChange={(e) => setChaveNova(e.target.value)}
                placeholder={form.api_key_definida ? '••••••••' : 'sk-…'}
                autoComplete="off"
                className={`${inputCls} pr-10`}
              />
              <button type="button" onClick={() => setMostrarChave(!mostrarChave)} className="absolute right-2 top-1/2 -translate-y-1/2">
                {mostrarChave ? <EyeOff className="w-5 h-5 text-gray-400" /> : <Eye className="w-5 h-5 text-gray-400" />}
              </button>
            </div>
            {form.api_key_definida && (
              <button type="button" onClick={limparChave} className="mt-2 text-xs text-red-500 hover:underline">
                Remover chave do cliente
              </button>
            )}
          </div>
        )}

        <p className={`text-xs flex items-start gap-1.5 ${textS}`}>
          <ShieldCheck className="w-4 h-4 shrink-0 mt-0.5" />
          A chave é gravada criptografada e nunca é devolvida pela API.
        </p>
      </div>

      <div className={`${card} ${cardPadding} space-y-3`}>
        <h3 className={`font-semibold ${textP}`}>Testar</h3>
        <div className="flex flex-col sm:flex-row gap-2">
          <input
            value={mensagemTeste}
            onChange={(e) => setMensagemTeste(e.target.value)}
            className={inputCls}
            placeholder="Mensagem de teste"
          />
          <button
            onClick={testar}
            disabled={testando}
            className="gradient-button px-4 py-2 rounded-lg text-sm flex items-center justify-center gap-2 shrink-0"
          >
            {testando ? <Loader2 className="w-4 h-4 animate-spin" /> : <Send className="w-4 h-4" />}
            Testar
          </button>
        </div>

        {resultadoTeste && (
          <div className={`rounded-lg p-3 text-sm ${
            resultadoTeste.ok
              ? darkMode ? 'bg-green-900/20 text-green-300' : 'bg-green-50 text-green-800'
              : darkMode ? 'bg-red-900/20 text-red-300' : 'bg-red-50 text-red-800'
          }`}>
            {resultadoTeste.ok ? (
              <>
                <div className="font-mono text-xs mb-2">
                  {resultadoTeste.provider} · {resultadoTeste.modelo} · chave: {resultadoTeste.chave_origem} · {resultadoTeste.latencia_ms}ms
                </div>
                <pre className="whitespace-pre-wrap break-words text-xs">{resultadoTeste.resposta_bruta}</pre>
              </>
            ) : (
              <div className="flex items-start gap-2">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span className="break-words">{resultadoTeste.erro}</span>
              </div>
            )}
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={salvar}
          disabled={salvando}
          className="gradient-button px-6 py-2 rounded-lg text-sm flex items-center gap-2"
        >
          {salvando && <Loader2 className="w-4 h-4 animate-spin" />}
          Salvar configuração
        </button>
      </div>
    </div>
  );
}
